import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { supabase } from '../../supabase';
import { useWorkspace } from '../../context/WorkspaceContext';
import { startOfMonth, endOfMonth, eachDayOfInterval, format, isSameDay, addMonths, subMonths, isBefore, startOfDay, addDays, subDays } from 'date-fns';
import { FiChevronLeft, FiChevronRight, FiCalendar, FiClock, FiMapPin } from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const MobileCalendar = () => {
    const { activeWorkspace } = useWorkspace();
    const [currentDate, setCurrentDate] = useState(new Date());
    const [selectedDate, setSelectedDate] = useState(new Date());
    const [events, setEvents] = useState([]);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (activeWorkspace) {
            fetchEvents();
        }
    }, [activeWorkspace, currentDate]);

    const fetchEvents = async () => {
        try {
            setLoading(true);
            const start = startOfMonth(currentDate).toISOString();
            const end = endOfMonth(currentDate).toISOString();

            // Fetch marketing events
            const { data: eventsData } = await supabase
                .from('marketing_events')
                .select('*')
                .eq('workspace_id', activeWorkspace.id)
                .gte('event_date', start)
                .lte('event_date', end);

            // Fetch social media posts
            const { data: postsData } = await supabase
                .from('social_media_posts')
                .select('*')
                .eq('workspace_id', activeWorkspace.id)
                .gte('scheduled_at', start)
                .lte('scheduled_at', end);

            // Fetch scheduled emails
            const { data: emailData } = await supabase
                .from('email_campaigns')
                .select('id, name, subject, scheduled_at, status')
                .eq('workspace_id', activeWorkspace.id)
                .gte('scheduled_at', start)
                .lte('scheduled_at', end);

            const socialEvents = (postsData || []).map(p => ({
                id: `post-${p.id}`,
                title: `Post: ${p.title || p.content.substring(0, 20)}`,
                description: p.content,
                event_date: p.scheduled_at,
                type: 'social_post',
                platform: p.platform,
                status: p.status
            }));

            const emailEvents = (emailData || []).map(e => ({
                id: `email-${e.id}`,
                title: `Email: ${e.name}`,
                description: e.subject,
                event_date: e.scheduled_at,
                type: 'email_campaign',
                status: e.status
            }));

            setEvents([...(eventsData || []), ...socialEvents, ...emailEvents]);
        } catch (err) {
            console.error('Error fetching mobile events:', err);
        } finally {
            setLoading(false);
        }
    };

    // ⚡ Bolt Optimization: Pre-group events by date string to replace O(N) array filtering.
    // Why: `getEventsForDay` was called multiple times per render (for each day in the week strip).
    // Filtering the entire `events` array every time resulted in O(7 * N) time complexity.
    // Impact: Reduces lookup to O(1) per day, significantly improving rendering performance on mobile.
    const eventsByDateStr = useMemo(() => {
        const map = {};
        events.forEach(event => {
            const dateStr = format(new Date(event.event_date), 'yyyy-MM-dd');
            if (!map[dateStr]) {
                map[dateStr] = [];
            }
            map[dateStr].push(event);
        });
        return map;
    }, [events]);

    const getEventsForDay = useCallback((date) => {
        const dateStr = format(date, 'yyyy-MM-dd');
        return eventsByDateStr[dateStr] || [];
    }, [eventsByDateStr]);

    const nextDay = () => setSelectedDate(addDays(selectedDate, 1));
    const prevDay = () => setSelectedDate(subDays(selectedDate, 1));

    // Generate a week strip centered on selected date
    const weekDays = [];
    for (let i = -3; i <= 3; i++) {
        weekDays.push(addDays(selectedDate, i));
    }

    const dayEvents = getEventsForDay(selectedDate);

    return (
        <div className="flex flex-col min-h-full bg-slate-50 dark:bg-black pt-[env(safe-area-inset-top)] transition-colors duration-200">
            {/* Header */}
            <div className="px-6 py-4 flex justify-between items-center bg-white dark:bg-slate-900 border-b border-slate-200 dark:border-slate-800 sticky top-0 z-10">
                <h1 className="text-xl font-bold text-slate-900 dark:text-white">Calendar</h1>
                <div className="text-sm font-medium text-slate-500 dark:text-slate-400">
                    {format(selectedDate, 'MMMM yyyy')}
                </div>
            </div>

            {/* Date Strip */}
            <div className="bg-white dark:bg-slate-900 py-4 border-b border-slate-200 dark:border-slate-800 shadow-sm">
                <div className="flex justify-between items-center px-4">
                    {weekDays.map((day, idx) => {
                        const isSelected = isSameDay(day, selectedDate);
                        const isToday = isSameDay(day, new Date());
                        const hasEvents = getEventsForDay(day).length > 0;

                        return (
                            <button
                                key={idx}
                                onClick={() => {
                                    setSelectedDate(day);
                                    if (day.getMonth() !== currentDate.getMonth()) {
                                        setCurrentDate(day);
                                    }
                                }}
                                className={`flex flex-col items-center justify-center w-10 h-14 rounded-xl transition-all ${
                                    isSelected
                                        ? 'bg-blue-600 text-white shadow-md transform scale-110'
                                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800'
                                }`}
                            >
                                <span className={`text-[10px] font-medium ${isSelected ? 'text-blue-100' : ''}`}>{format(day, 'EEE')}</span>
                                <span className={`text-base font-bold mt-0.5 relative`}>
                                    {format(day, 'd')}
                                    {hasEvents && !isSelected && (
                                        <span className="absolute -bottom-1.5 left-1/2 transform -translate-x-1/2 w-1 h-1 bg-blue-500 rounded-full"></span>
                                    )}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* Events List */}
            <div className="flex-1 px-4 py-6 space-y-4">
                <h2 className="text-sm font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider px-2">
                    {format(selectedDate, 'EEEE, MMM do')}
                </h2>

                {loading ? (
                    <div className="text-center py-10 text-slate-400">Loading events...</div>
                ) : dayEvents.length === 0 ? (
                    <div className="text-center py-12 bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 border-dashed mx-2">
                        <SafeIcon icon={FiCalendar} className="text-4xl text-slate-300 dark:text-slate-600 mx-auto mb-3" />
                        <p className="text-slate-500 dark:text-slate-400 text-sm">No events scheduled.</p>
                    </div>
                ) : (
                    dayEvents.map(event => (
                        <div key={event.id} className="bg-white dark:bg-slate-800 p-4 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm flex gap-4">
                            <div className="flex flex-col items-center">
                                <div className="text-xs font-bold text-slate-400">{format(new Date(event.event_date), 'h:mm')}</div>
                                <div className="text-xs text-slate-400">{format(new Date(event.event_date), 'a')}</div>
                                <div className={`w-0.5 h-full bg-slate-200 dark:bg-slate-700 mt-2 ${dayEvents.indexOf(event) === dayEvents.length - 1 ? 'hidden' : ''}`}></div>
                            </div>
                            <div className="flex-1 pb-2">
                                <h3 className="font-bold text-slate-900 dark:text-white text-base">{event.title}</h3>
                                {event.description && (
                                    <p className="text-sm text-slate-500 dark:text-slate-400 mt-1 line-clamp-2">{event.description}</p>
                                )}
                                <div className="flex items-center gap-2 mt-3">
                                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium border ${
                                        event.type === 'social_post' ? 'bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 border-blue-100 dark:border-blue-800' :
                                        event.type === 'email_campaign' ? 'bg-purple-50 dark:bg-purple-900/30 text-purple-600 dark:text-purple-300 border-purple-100 dark:border-purple-800' :
                                        'bg-slate-100 dark:bg-slate-700 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-600'
                                    }`}>
                                        {event.type === 'social_post' ? 'Social Post' : event.type === 'email_campaign' ? 'Email' : 'Event'}
                                    </span>
                                    {event.status && (
                                        <span className="text-[10px] text-slate-400 capitalize">• {event.status}</span>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
    );
};

export default MobileCalendar;
